import type { ScenarioConfiguration } from "src/service/cost/data/building";
import type { ProjectCostingCalculationHttpResource } from "src/service/cost/resource/project/project-costing-calculation-resource";
import type { ProjectScenarioHttpResourceWithEverything } from "src/service/project/resource/project-scenario";
import type { ProjectScenarioDesignOptionHttpResource } from "src/service/project/resource/project-scenario-design-option";
import type { ProjectHttpResource } from "src/service/project/resource/project";
import type { ProjectScenarioDesignOptionSiteHttpResource } from "src/service/project/resource/project-scenario-design-option-site";
import { calculateCost } from "src/workers/cost-engine";
import type { WorkspaceConfig } from "src/hooks/useWorkspaceQuery";
import { World } from "./World";
import { Compress } from "src/utils/Compress";
import { makeAutoObservable } from "mobx";

export interface BeScenarioWithAggDesignDtoZip {
  aggDesignDtoZip: Uint8Array;
  sites: ProjectScenarioDesignOptionSiteHttpResource[];
  label: string;
}

type StoredDesign = ProjectScenarioDesignOptionHttpResource & {
  _projectId: string;
  _scenarioId: string;
};

type CostSettings = { contingency: number; ohp: number; prelims: number };

const LS_PROJECTS = "tessa:projects";
const LS_SCENARIOS = "tessa:scenarios";
const LS_DESIGNS = "tessa:designs";
const LS_WORKSPACE = "tessa:workspace";
const LS_COST_SETTINGS = "tessa:cost-settings";

const DEFAULT_COST_SETTINGS: CostSettings = { contingency: 3, ohp: 5.5, prelims: 10 };

export class Api {
  constructor() {
    makeAutoObservable(this);
  }

  // ── localStorage helpers ────────────────────────────────────────────────────

  private static lsRead<T>(key: string): T | null {
    try {
      const val = localStorage.getItem(key);
      return val ? (JSON.parse(val) as T) : null;
    } catch {
      return null;
    }
  }

  private static lsWrite<T>(key: string, value: T): void {
    localStorage.setItem(key, JSON.stringify(value));
  }

  private static now(): string {
    return new Date().toISOString();
  }

  private static uuid(): string {
    return crypto.randomUUID();
  }

  // ── Projects ────────────────────────────────────────────────────────────────

  async GetProjectCollection() {
    const projects = Api.lsRead<Record<string, ProjectHttpResource>>(LS_PROJECTS) ?? {};
    const results = Object.values(projects).sort((a, b) =>
      b.created_at.localeCompare(a.created_at)
    );
    return { results, pagination: { limit: 100, offset: 0, total: results.length } };
  }

  async GetProject({ projectId }: { projectId: string }) {
    const projects = Api.lsRead<Record<string, ProjectHttpResource>>(LS_PROJECTS) ?? {};
    return projects[projectId] ?? null;
  }

  async PostProject({ label }: { label: string }) {
    const projects = Api.lsRead<Record<string, ProjectHttpResource>>(LS_PROJECTS) ?? {};
    const now = Api.now();
    const project: ProjectHttpResource = { id: Api.uuid(), label, notes: null, created_at: now, updated_at: now };
    projects[project.id] = project;
    Api.lsWrite(LS_PROJECTS, projects);
    return project;
  }

  async PatchProject({ projectId, label, notes }: { projectId: string; label: string; notes: string }) {
    const projects = Api.lsRead<Record<string, ProjectHttpResource>>(LS_PROJECTS) ?? {};
    const existing = projects[projectId];
    if (!existing) return null;
    const updated: ProjectHttpResource = { ...existing, label, notes, updated_at: Api.now() };
    projects[projectId] = updated;
    Api.lsWrite(LS_PROJECTS, projects);
    return updated;
  }

  async DeleteProject({ projectId }: { projectId: string }) {
    const projects = Api.lsRead<Record<string, ProjectHttpResource>>(LS_PROJECTS) ?? {};
    delete projects[projectId];
    Api.lsWrite(LS_PROJECTS, projects);

    const scenarios = Api.lsRead<Record<string, ProjectScenarioHttpResourceWithEverything>>(LS_SCENARIOS) ?? {};
    for (const sid of Object.keys(scenarios)) {
      if (scenarios[sid].project.id === projectId) delete scenarios[sid];
    }
    Api.lsWrite(LS_SCENARIOS, scenarios);

    const designs = Api.lsRead<Record<string, StoredDesign>>(LS_DESIGNS) ?? {};
    for (const did of Object.keys(designs)) {
      if (designs[did]._projectId === projectId) delete designs[did];
    }
    Api.lsWrite(LS_DESIGNS, designs);
  }

  // ── Scenarios ───────────────────────────────────────────────────────────────

  async GetScenarioCollection({ projectId }: { projectId: string }) {
    const scenarios =
      Api.lsRead<Record<string, ProjectScenarioHttpResourceWithEverything>>(LS_SCENARIOS) ?? {};
    const results = Object.values(scenarios)
      .filter((s) => s.project.id === projectId)
      .sort((a, b) => a.created_at.localeCompare(b.created_at));
    return { results, pagination: { limit: 100, offset: 0, total: results.length } };
  }

  async PostScenario(projectId: string) {
    const scenarios =
      Api.lsRead<Record<string, ProjectScenarioHttpResourceWithEverything>>(LS_SCENARIOS) ?? {};
    const now = Api.now();
    const scenario: ProjectScenarioHttpResourceWithEverything = {
      id: Api.uuid(),
      project: { id: projectId },
      editor: { origin_design_option_id: null, aggregation: { file: null } },
      brief: { building_separation: 7 },
      sites: [],
      created_at: now,
      updated_at: now,
    };
    scenarios[scenario.id] = scenario;
    Api.lsWrite(LS_SCENARIOS, scenarios);
    await this.PutScenarioWithWorldFile(projectId, scenario.id, new World());
    return scenario;
  }

  async GetScenarioWithWorldFile(projectId: string): Promise<BeScenarioWithAggDesignDtoZip | null> {
    const scenarios =
      Api.lsRead<Record<string, ProjectScenarioHttpResourceWithEverything>>(LS_SCENARIOS) ?? {};
    const beScenario = Object.values(scenarios).find((s) => s.project.id === projectId);
    if (!beScenario) return null;
    const aggDesignDtoZip = await this.loadFile(`scenario:${beScenario.id}`);
    if (!aggDesignDtoZip) throw new Error("world file not found in local storage");
    return { label: "", ...beScenario, aggDesignDtoZip };
  }

  async PutScenarioWithWorldFile(_projectId: string, scenarioId: string, world: World) {
    await this.storeFile(`scenario:${scenarioId}`, Compress(world.aggDesignOptionDto));
    const scenarios =
      Api.lsRead<Record<string, ProjectScenarioHttpResourceWithEverything>>(LS_SCENARIOS) ?? {};
    if (scenarios[scenarioId]) {
      scenarios[scenarioId] = {
        ...scenarios[scenarioId],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        brief: world.beBrief as any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        sites: world.beSites as any,
        updated_at: Api.now(),
      };
      Api.lsWrite(LS_SCENARIOS, scenarios);
    }
  }

  // ── Designs ─────────────────────────────────────────────────────────────────

  async GetDesignCollection({ scenarioId }: { scenarioId: string; projectId: string }) {
    const designs = Api.lsRead<Record<string, StoredDesign>>(LS_DESIGNS) ?? {};
    const results = Object.values(designs)
      .filter((d) => d._scenarioId === scenarioId)
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
    return { results, pagination: { limit: 100, offset: 0, total: results.length } };
  }

  async PostDesign(projectId: string, scenarioId: string, world: World): Promise<string | undefined> {
    const designs = Api.lsRead<Record<string, StoredDesign>>(LS_DESIGNS) ?? {};
    const now = Api.now();
    const design: StoredDesign = {
      id: Api.uuid(),
      label: world.label,
      aggregation: { file: { id: Api.uuid(), url: "" } },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      brief: world.beBrief as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sites: world.beSites as any,
      created_at: now,
      updated_at: now,
      _projectId: projectId,
      _scenarioId: scenarioId,
    };
    designs[design.id] = design;
    Api.lsWrite(LS_DESIGNS, designs);
    await this.storeFile(`design:${design.id}`, Compress(world.aggDesignOptionDto));
    return design.id;
  }

  async GetDesignWithWorldFile(
    _projectId: string,
    _scenarioId: string,
    designId: string
  ): Promise<BeScenarioWithAggDesignDtoZip | null> {
    const designs = Api.lsRead<Record<string, StoredDesign>>(LS_DESIGNS) ?? {};
    const body = designs[designId];
    if (!body) return null;
    const aggDesignDtoZip = await this.loadFile(`design:${designId}`);
    if (!aggDesignDtoZip) return null;
    return { ...body, aggDesignDtoZip };
  }

  async PatchDesign(params: { projectId: string; designId: string; label: string; scenarioId: string }) {
    const designs = Api.lsRead<Record<string, StoredDesign>>(LS_DESIGNS) ?? {};
    const existing = designs[params.designId];
    if (existing) {
      designs[params.designId] = { ...existing, label: params.label, updated_at: Api.now() };
      Api.lsWrite(LS_DESIGNS, designs);
    }
  }

  async DeleteDesign(props: { projectId: string; scenarioId: string; designId: string }) {
    const designs = Api.lsRead<Record<string, StoredDesign>>(LS_DESIGNS) ?? {};
    delete designs[props.designId];
    Api.lsWrite(LS_DESIGNS, designs);
  }

  async PutDesignExport(_payload: {
    projectId: string;
    scenarioId: string;
    designId: string;
    buildingsIds: string[];
    unitsCount: number;
  }) {
    // no-op in local mode
    return {};
  }

  // ── Workspace ───────────────────────────────────────────────────────────────

  async GetWorkspaceLayout(): Promise<WorkspaceConfig> {
    return Api.lsRead<WorkspaceConfig>(LS_WORKSPACE) as WorkspaceConfig;
  }

  async PostWorkspaceLayout(payload: WorkspaceConfig) {
    Api.lsWrite(LS_WORKSPACE, payload);
    return payload;
  }

  // ── Cost ────────────────────────────────────────────────────────────────────

  async GetCostingSettings(projectId: string) {
    const settings = Api.lsRead<Record<string, CostSettings>>(LS_COST_SETTINGS) ?? {};
    return settings[projectId] ?? { ...DEFAULT_COST_SETTINGS };
  }

  async PatchCosting(projectId: string, payload: CostSettings) {
    const settings = Api.lsRead<Record<string, CostSettings>>(LS_COST_SETTINGS) ?? {};
    settings[projectId] = payload;
    Api.lsWrite(LS_COST_SETTINGS, settings);
    return payload;
  }

  async PostCostingCalculation(
    projectId: string,
    payload: ScenarioConfiguration
  ): Promise<ProjectCostingCalculationHttpResource> {
    const settings = await this.GetCostingSettings(projectId);
    return calculateCost(payload, {
      prelims: settings.prelims,
      contingency: settings.contingency,
      ohp: settings.ohp,
    });
  }

  // ── User (hardcoded) ────────────────────────────────────────────────────────

  async GetUser(_: { userId: string }) {
    return {
      created_at: "2024-01-01T00:00:00Z",
      email: "user@tessa.ai",
      id: "local-user",
      is_active: true,
      is_master_admin: false,
      last_active_at: "2024-01-01T00:00:00Z",
      profile: {
        name_first: "Local",
        name_last: "User",
        company_job_title: "",
        company_name: "",
      },
      updated_at: "2024-01-01T00:00:00Z",
    };
  }

  // ── IndexedDB helpers ────────────────────────────────────────────────────────

  private static openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open("tessa-world-files", 1);
      req.onupgradeneeded = () => req.result.createObjectStore("files");
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  private async storeFile(key: string, data: Uint8Array): Promise<void> {
    const db = await Api.openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction("files", "readwrite");
      tx.objectStore("files").put(data, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  private async loadFile(key: string): Promise<Uint8Array | null> {
    const db = await Api.openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction("files", "readonly");
      const req = tx.objectStore("files").get(key);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => reject(req.error);
    });
  }
}
